import { forwardRef, useCallback, useMemo, useRef } from 'react';

import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { X as CloseIcon, Loader2 } from 'lucide-react';

import { findingTypePickerDropdownArrowIconSx } from '../../Findings/findingStyles';
import { useLanguage } from '../../../hooks';
import { useNestedLocationStore } from '../../../newStore/nestedLocationStore';
import { useLocationsByIdsQuery } from '../../../queries';
import { NestedLocation } from '../../../types/nestedLocations';
import { cn } from '../../../utils/utils';
import { LocationIcon } from '../LocationIcon';
import { SelectedLocations } from './LocationsSelectorReducer';

interface LocationSelectorTriggerProps {
  selectedLocations: SelectedLocations;
  legacySelectedLocations: number[];
  multipleSelection: boolean;
  triggerPlaceholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  onSelectLocation?: (locationId: number, locationType: 'NestableLocation' | 'Location') => void;
  prefixBadge?: string;
  /** Forwarded onto the trigger `<button>` via `...props` for agent-browser targeting. */
  'data-testid'?: string;
}

interface AllLocationsItem {
  id: number;
  name: string;
  nestableLocation: NestedLocation | null;
}

const MAX_DISPLAYED_LOCATIONS = 20;

export const LocationSelectorTrigger = forwardRef<HTMLButtonElement, LocationSelectorTriggerProps>(
  (
    {
      selectedLocations,
      legacySelectedLocations,
      multipleSelection,
      triggerPlaceholder,
      triggerClassName,
      disabled,
      onSelectLocation,
      prefixBadge,
      ...props
    },
    ref,
  ) => {
    const { locationsIndex: nestedLocationIndex } = useNestedLocationStore();
    const lang = useLanguage();
    const containerRef = useRef<HTMLDivElement>(null);
    const badgesRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const nestableLocations = useMemo(
      () =>
        Array.from(selectedLocations.entries())
          .filter(([, value]) => value === 'selected')
          .map(([key]) => nestedLocationIndex.locationById.get(Number(key)) || null),
      [selectedLocations, nestedLocationIndex.locationById],
    );

    const { data: legacyLocations = [], isLoading: isLegacyLocationsLoading } =
      useLocationsByIdsQuery(legacySelectedLocations);

    const allLocations: Array<AllLocationsItem> = useMemo(
      () => [
        ...nestableLocations
          .filter((loc): loc is NestedLocation => loc !== null)
          .map((loc) => ({
            id: loc?.id,
            name: loc?.name,
            nestableLocation: loc,
          })),
        ...legacyLocations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          nestableLocation: null as NestedLocation | null,
        })),
      ],
      [nestableLocations, legacyLocations],
    );

    const hasSelection = allLocations.length > 0;

    const handleRemoveLocation = (
      event: React.MouseEvent<HTMLSpanElement> | React.PointerEvent<HTMLSpanElement>,
      locationId: number,
      locationType: 'NestableLocation' | 'Location',
    ) => {
      event.stopPropagation();
      event.preventDefault();
      onSelectLocation?.(locationId, locationType);
    };

    const renderLocationBadge = useCallback(
      (item: AllLocationsItem) => {
        return (
          <div
            key={item.id}
            ref={(element) => badgesRefs.current.set(item.id, element)}
            className='flex items-center h-7 gap-1 px-2 py-0.5 bg-gray-100 rounded-md shrink-0'
          >
            {item.nestableLocation && (
              <LocationIcon placeType={item.nestableLocation.place_type_id} />
            )}
            <span className='whitespace-nowrap'>{item.name}</span>
            <span
              onPointerDown={(event) =>
                handleRemoveLocation(
                  event,
                  item.id,
                  item.nestableLocation ? 'NestableLocation' : 'Location',
                )
              }
              className='ml-1 hover:bg-gray-200 rounded-full p-0.5 transition-colors cursor-pointer'
              aria-label='Remove location'
              role='button'
              tabIndex={0}
            >
              <CloseIcon
                size={12}
                className='text-gray-600'
              />
            </span>
          </div>
        );
      },
      [handleRemoveLocation],
    );

    const renderPrefixBadge = useCallback(() => {
      if (!prefixBadge) return null;

      return (
        <div className='flex items-center h-7 gap-1 px-2 py-0.5 bg-gray-100 rounded-md shrink-0'>
          <span className='whitespace-nowrap'>{prefixBadge}</span>
        </div>
      );
    }, [prefixBadge]);

    const renderSelectedLocations = useCallback(() => {
      if (isLegacyLocationsLoading) {
        return <Loader2 className='animate-spin text-primary' />;
      }

      if (!hasSelection && !prefixBadge) {
        return (
          <span className='truncate'>
            {triggerPlaceholder || lang.form_answer.multi_option_placeholder}
          </span>
        );
      }

      if (multipleSelection) {
        const displayedLocations = allLocations.slice(0, MAX_DISPLAYED_LOCATIONS);
        return (
          <>
            {renderPrefixBadge()}
            {displayedLocations.map((item) => renderLocationBadge(item))}
            {allLocations.length > MAX_DISPLAYED_LOCATIONS && (
              <span className='text-gray-500 text-sm'>
                +{allLocations.length - displayedLocations.length}{' '}
                {lang.commonComponents.formSelector.more}
              </span>
            )}
          </>
        );
      }

      if (!hasSelection && prefixBadge) {
        return renderPrefixBadge();
      }

      return (
        <div className='flex items-center gap-1 py-0.5 rounded-md w-full'>
          {allLocations[0].nestableLocation && (
            <div className='shrink-0'>
              <LocationIcon placeType={allLocations[0].nestableLocation.place_type_id} />
            </div>
          )}
          <span className='truncate'>{allLocations[0].name}</span>
        </div>
      );
    }, [
      allLocations,
      isLegacyLocationsLoading,
      multipleSelection,
      renderLocationBadge,
      prefixBadge,
      renderPrefixBadge,
    ]);

    return (
      <button
        type='button'
        className={cn(
          'relative w-full border border-gray-200 rounded-lg flex items-center pl-3 py-1 pr-12 h-8 cursor-pointer hover:border-primary focus:outline-none focus:ring-0',
          disabled && 'opacity-80 cursor-default hover:border-gray-200',
          multipleSelection ? 'flex-wrap h-auto px-4 py-2' : '',
          triggerClassName,
        )}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            'text-sm flex items-center gap-1.5 flex-1 min-w-0 flex-wrap',
            hasSelection ? 'text-gray-700' : 'text-[#B8BABF]',
          )}
          ref={containerRef}
        >
          {renderSelectedLocations()}
        </div>
        <KeyboardArrowDownRounded
          sx={{
            ...findingTypePickerDropdownArrowIconSx,
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        />
      </button>
    );
  },
);

LocationSelectorTrigger.displayName = 'LocationSelectorTrigger';
